import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useCallback } from 'react';

// LocalStorage keys for fallback
const LOCAL_SELECTED_KEY = 'selectedPledgeSheetId';
const LOCAL_STRETCHED_KEY = 'useStretchedGoals';

export interface PledgePreferences {
  selectedPledgeSheetId: string | null;
  useStretchedGoals: boolean;
}

// Helper: read from localStorage
const readLocal = (): PledgePreferences => {
  const localSelected = localStorage.getItem(LOCAL_SELECTED_KEY);
  const localStretched = localStorage.getItem(LOCAL_STRETCHED_KEY) === 'true';
  return {
    selectedPledgeSheetId: localSelected || null,
    useStretchedGoals: localStretched,
  };
};

// Helper: write to localStorage
const writeLocal = (prefs: PledgePreferences) => {
  if (prefs.selectedPledgeSheetId) {
    localStorage.setItem(LOCAL_SELECTED_KEY, prefs.selectedPledgeSheetId);
  } else {
    localStorage.removeItem(LOCAL_SELECTED_KEY);
  }
  localStorage.setItem(LOCAL_STRETCHED_KEY, String(prefs.useStretchedGoals));
};

const fetchPreferences = async (userId: string | undefined): Promise<PledgePreferences> => {
  if (!userId) {
    return readLocal();
  }

  const { data, error } = await supabase
    .from('pledge_preferences')
    .select('selected_pledge_sheet_id, use_stretched_goals')
    .eq('user_id', userId)
    .maybeSingle();

  if (error) {
    console.warn('Failed to fetch pledge preferences:', error.message);
    return readLocal();
  }

  if (!data) {
    // No row yet, check local and migrate if needed
    const local = readLocal();
    if (local.selectedPledgeSheetId !== null || local.useStretchedGoals !== false) {
      // Create row with local values
      await supabase.from('pledge_preferences').insert({
        user_id: userId,
        selected_pledge_sheet_id: local.selectedPledgeSheetId,
        use_stretched_goals: local.useStretchedGoals,
      });
    }
    return local;
  }

  return {
    selectedPledgeSheetId: data.selected_pledge_sheet_id ?? null,
    useStretchedGoals: !!data.use_stretched_goals,
  };
};

export function usePledgePreferencesQuery() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: prefs, isLoading: loading } = useQuery({
    queryKey: ['pledge_preferences', user?.id],
    queryFn: () => fetchPreferences(user?.id),
    staleTime: 15 * 60 * 1000, // 15 minutes - optimized for better performance
    gcTime: 45 * 60 * 1000, // 45 minutes
  });

  const updateMutation = useMutation({
    mutationFn: async (newPrefs: PledgePreferences) => {
      // Always write to localStorage first
      writeLocal(newPrefs);

      if (!user) return newPrefs;

      const { error } = await supabase.from('pledge_preferences').upsert({
        user_id: user.id,
        selected_pledge_sheet_id: newPrefs.selectedPledgeSheetId,
        use_stretched_goals: newPrefs.useStretchedGoals,
      });

      if (error) {
        console.warn('Failed to save pledge preferences:', error.message);
      }

      return newPrefs;
    },
    // Optimistic update - immediately update cache before server response
    onMutate: async (newPrefs: PledgePreferences) => {
      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: ['pledge_preferences', user?.id] });
      
      // Snapshot the previous value
      const previousPrefs = queryClient.getQueryData<PledgePreferences>(['pledge_preferences', user?.id]);
      
      // Immediately update the cache with new values
      queryClient.setQueryData(['pledge_preferences', user?.id], newPrefs);
      
      // Return context with the previous value for potential rollback
      return { previousPrefs };
    },
    onError: (_err, _newPrefs, context) => {
      // If the mutation fails, rollback to the previous value
      if (context?.previousPrefs) {
        queryClient.setQueryData(['pledge_preferences', user?.id], context.previousPrefs);
      }
    },
    onSettled: () => {
      // Refetch after error or success to ensure sync with server
      queryClient.invalidateQueries({ queryKey: ['pledge_preferences', user?.id] });
    }
  });

  const setSelectedPledgeSheetId = useCallback((id: string | null) => {
    const newPrefs: PledgePreferences = {
      selectedPledgeSheetId: id,
      useStretchedGoals: prefs?.useStretchedGoals || false,
    };
    updateMutation.mutate(newPrefs);
  }, [prefs?.useStretchedGoals, updateMutation]);

  const setUseStretchedGoals = useCallback((use: boolean) => {
    const newPrefs: PledgePreferences = {
      selectedPledgeSheetId: prefs?.selectedPledgeSheetId || null,
      useStretchedGoals: use,
    };
    updateMutation.mutate(newPrefs);
  }, [prefs?.selectedPledgeSheetId, updateMutation]);

  const clearPreferences = useCallback(() => {
    const newPrefs: PledgePreferences = {
      selectedPledgeSheetId: null,
      useStretchedGoals: false,
    };
    updateMutation.mutate(newPrefs);
  }, [updateMutation]);

  return {
    loading,
    selectedPledgeSheetId: prefs?.selectedPledgeSheetId || null,
    useStretchedGoals: prefs?.useStretchedGoals || false,
    setSelectedPledgeSheetId,
    setUseStretchedGoals,
    clearPreferences,
  };
}
